import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import type { OrgMembershipRole, OrgMembershipStatus } from '@/types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export interface OrgMembershipSummary {
  id: string;
  name: string;
  slug: string;
  orgFeePercent: number;
  createdAt: string;
  updatedAt: string;
  membershipId: string;
  membershipRole: OrgMembershipRole;
  membershipStatus: OrgMembershipStatus;
}

async function fetchOrgMemberships(): Promise<OrgMembershipSummary[]> {
  const res = await fetch(`${API_BASE}/api/orgs/mine`, {
    credentials: 'include',
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || 'Failed to load organizations');
  }

  const data = (await res.json()) as { data: OrgMembershipSummary[] };
  return data.data || [];
}

export function useOrgMemberships(options?: { enabled?: boolean }) {
  const { userId } = useAuth();

  return useQuery({
    queryKey: ['org-memberships', userId],
    queryFn: fetchOrgMemberships,
    enabled: (options?.enabled ?? true) && !!userId,
    staleTime: 1000 * 60 * 5,
  });
}
